import { APP_URLS } from "@shared/constants.urls";

type MenuItem = {
  id: number;
  title: string;
  slug: string;
  url: string;
  showNotification?: boolean;
  children?: MenuItem[];
  position?: number;
};

type Flags = {
  showMemberBenefits?: boolean;
};

type MenuOptions = {
  isMember: boolean;
  flags?: Flags;
};

const yourFavouritesChildren = ["just-for-you", "order-it-again"];

const allMenuItemsBase: Record<string, MenuItem> = {
  "order-history": {
    id: 1,
    title: "Order History",
    slug: "order-history",
    url: APP_URLS.orderHistory
  },
  "order-it-again": {
    id: 2,
    title: "Order It Again",
    slug: "order-it-again",
    url: APP_URLS.orderItAgain,
    showNotification: true
  },
  "your-favourites": {
    id: 3,
    title: "Your Favourites",
    slug: "your-favourites",
    url: APP_URLS.forYou
  },
  "just-for-you": {
    id: 4,
    title: "Just For You",
    slug: "just-for-you",
    url: APP_URLS.forYou
  },
  wishlist: {
    id: 5,
    title: "Wishlist",
    slug: "wishlist",
    url: APP_URLS.wishlist
  },
  "membership-billing": {
    id: 6,
    title: "Membership & Billing",
    slug: "membership-billing",
    url: APP_URLS.myMembership
  },
  "address-book": {
    id: 7,
    title: "Address Book",
    slug: "address-book",
    url: APP_URLS.addressBook
  },
  profile: {
    id: 8,
    title: "Profile",
    slug: "profile",
    url: APP_URLS.profile
  },
  "refer-a-friend": {
    id: 9,
    title: "Refer a Friend",
    slug: "refer-a-friend",
    url: APP_URLS.referAFriend
  },
  "member-benefits": {
    id: 10,
    title: "Member Perks",
    slug: "member-benefits",
    url: APP_URLS.memberBenefits
  }
};

const allMenuItems: Record<string, MenuItem> = {
  ...allMenuItemsBase,
  "your-favourites": {
    ...allMenuItemsBase["your-favourites"],
    children: yourFavouritesChildren.map((slug) => allMenuItemsBase[slug])
  }
};

const nonMemberSlugs = [
  "order-history",
  "wishlist",
  "membership-billing",
  "address-book",
  "profile"
];

const memberExtrasForUserMenu = [
  "order-it-again",
  "just-for-you",
  "refer-a-friend",
  "member-benefits" // conditional
];

const memberExtrasForAccount = [
  "your-favourites",
  "refer-a-friend",
  "member-benefits" // conditional
];

const getSlugs = (
  isMember: boolean,
  base: string[],
  memberExtras: string[]
): string[] => {
  return isMember ? [...base, ...memberExtras] : base;
};

const toOrderedMenuItems = (a: MenuItem, b: MenuItem) => {
  return (a.position ?? a.id) - (b.position ?? b.id);
};

const buildMenu = (slugs: string[], flags: Flags): MenuItem[] => {
  return slugs
    .map((slug): MenuItem | null => {
      const item = allMenuItems[slug];
      if (!item) return null;
      if (slug === "member-benefits" && !flags.showMemberBenefits) return null;

      let children = item.children;
      if (children?.length) {
        children = children
          .filter((child) => {
            if (child.slug === "member-benefits" && !flags.showMemberBenefits)
              return false;
            return true;
          })
          .sort(toOrderedMenuItems);
      }

      return {
        ...item,
        children
      };
    })
    .filter((item): item is MenuItem => item !== null)
    .sort(toOrderedMenuItems);
};

export const getUserMenuItems = ({
  isMember,
  flags = {}
}: MenuOptions): MenuItem[] => {
  const slugs = getSlugs(isMember, nonMemberSlugs, memberExtrasForUserMenu);
  return buildMenu(slugs, flags);
};

export const getAccountItems = ({
  isMember,
  flags = {}
}: MenuOptions): MenuItem[] => {
  const slugs = getSlugs(isMember, nonMemberSlugs, memberExtrasForAccount);
  return buildMenu(slugs, flags);
};
